/**
 * v0.9.286: Theme system.
 *
 * The POS originally hardcoded one warm/earthy palette in tokens.ts, imported
 * as a static `colors` object across ~49 files. To make the palette swappable
 * at runtime WITHOUT rewriting all those files, tokens.ts now exports `colors`
 * as a set of CSS-variable references (var(--c-NAME)). This module defines the
 * actual hex values per theme and injects them as custom properties on :root,
 * so changing the theme re-paints the whole app instantly - every existing
 * `colors.primary` reference resolves to the new value with zero code churn.
 *
 * Adding a theme = add an entry to THEMES with the same key set as ThemePalette.
 */

export type ThemePalette = {
  bg: string;
  surface: string;
  surfaceAlt: string;
  surfaceHigh: string;
  surfaceInset: string;
  border: string;
  borderDim: string;
  borderFocus: string;
  text: string;
  textDim: string;
  textMuted: string;
  textInverse: string;
  primary: string;
  primaryHover: string;
  primaryDark: string;
  primaryDim: string;
  primaryFaint: string;
  success: string;
  successLight: string;
  successFaint: string;
  danger: string;
  dangerDark: string;
  dangerFaint: string;
  warning: string;
  warningLight: string;
  info: string;
  infoDark: string;
  purple: string;
  white: string;
  black: string;
};

export type ThemeId = 'nexttab' | 'warm' | 'hardware' | 'light' | 'midnight';

export type ThemeMeta = { id: ThemeId; name: string; blurb: string; swatch: string[] };

/** Original warm/earthy palette - the product's heritage look. */
const warm: ThemePalette = {
  bg: '#0c1118',
  surface: '#1a1610',
  surfaceAlt: '#1d1c18',
  surfaceHigh: '#2a2a28',
  surfaceInset: '#0c0b09',
  border: '#3a3830',
  borderDim: '#2a2a28',
  borderFocus: '#a8832f',
  text: '#e8e0d0',
  textDim: '#d4c9b8',
  textMuted: '#8a7a66',
  textInverse: '#0c0b09',
  primary: '#d4a843',
  primaryHover: '#e8a22e',
  primaryDark: '#a8832f',
  primaryDim: '#8a6c2a',
  primaryFaint: '#3a3014',
  success: '#3a9a5c',
  successLight: '#52c07a',
  successFaint: '#0e3a1f',
  danger: '#d95f5f',
  dangerDark: '#c04040',
  dangerFaint: '#5a1212',
  warning: '#e05c2a',
  warningLight: '#e8874a',
  info: '#5b8def',
  infoDark: '#3a8fd4',
  purple: '#9b72e8',
  white: '#faf8f4',
  black: '#0c0b09',
};

/**
 * High-contrast dark - the "hardware" look. Near-black panels, brighter
 * borders, punchier accent. Heavier and more tactile; reads like a dedicated
 * terminal rather than a web page.
 */
const hardware: ThemePalette = {
  bg: '#000000',
  surface: '#121317',
  surfaceAlt: '#171a1f',
  surfaceHigh: '#22262e',
  surfaceInset: '#070809',
  border: '#3c4250',
  borderDim: '#262b34',
  borderFocus: '#f0b429',
  text: '#f7fafc',
  textDim: '#cbd5e0',
  textMuted: '#8a94a6',
  textInverse: '#000000',
  primary: '#f5b300',
  primaryHover: '#ffc524',
  primaryDark: '#c98f00',
  primaryDim: '#9c7000',
  primaryFaint: '#2b2410',
  success: '#22c55e',
  successLight: '#4ade80',
  successFaint: '#0c2f1a',
  danger: '#ef4444',
  dangerDark: '#dc2626',
  dangerFaint: '#3f1212',
  warning: '#f97316',
  warningLight: '#fb923c',
  info: '#3b82f6',
  infoDark: '#2563eb',
  purple: '#a855f7',
  white: '#ffffff',
  black: '#000000',
};

/** Clean light - bright daytime counter look. */
const light: ThemePalette = {
  bg: '#f2f0ec',
  surface: '#ffffff',
  surfaceAlt: '#f7f5f1',
  surfaceHigh: '#ffffff',
  surfaceInset: '#eceae5',
  border: '#d6d1c7',
  borderDim: '#e4e0d8',
  borderFocus: '#c8922a',
  text: '#1c1a16',
  textDim: '#3f3a32',
  textMuted: '#7c756a',
  textInverse: '#ffffff',
  primary: '#b8862b',
  primaryHover: '#a87822',
  primaryDark: '#8a6420',
  primaryDim: '#a9935f',
  primaryFaint: '#f3e9cf',
  success: '#1f8a4c',
  successLight: '#2faa60',
  successFaint: '#dff3e6',
  danger: '#c43d3d',
  dangerDark: '#a82f2f',
  dangerFaint: '#f7dede',
  warning: '#cf5a1f',
  warningLight: '#e07a3a',
  info: '#2f6fd0',
  infoDark: '#2459ad',
  purple: '#7c4fd0',
  white: '#ffffff',
  black: '#0c0b09',
};

/** Midnight - colorful/branded. Deep indigo base with violet+cyan accents. */
const midnight: ThemePalette = {
  bg: '#0b0e1f',
  surface: '#161a33',
  surfaceAlt: '#1c2140',
  surfaceHigh: '#262c52',
  surfaceInset: '#080a16',
  border: '#3a4170',
  borderDim: '#262c52',
  borderFocus: '#7c5cff',
  text: '#eef0ff',
  textDim: '#c4c8e8',
  textMuted: '#8087b0',
  textInverse: '#0b0e1f',
  primary: '#7c5cff',
  primaryHover: '#9277ff',
  primaryDark: '#5d3fe0',
  primaryDim: '#4a35a8',
  primaryFaint: '#221c45',
  success: '#2dd4bf',
  successLight: '#5eead4',
  successFaint: '#0c322e',
  danger: '#fb5e7e',
  dangerDark: '#e23a60',
  dangerFaint: '#3d1320',
  warning: '#fbbf24',
  warningLight: '#fcd34d',
  info: '#38bdf8',
  infoDark: '#0ea5e9',
  purple: '#c084fc',
  white: '#ffffff',
  black: '#0b0e1f',
};


/**
 * v0.9.1480: NextTab - the brand palette. Pulled from the logo: black,
 * white, cobalt blue (#0050e0 in the mark, lifted to #3b7dff on black so
 * body-size text clears 4.5:1), cool greys. Same structure as Hardware;
 * only the accent family and its dependents change. `info` moves to teal
 * so "informational" chrome (transfer, section chips) still reads as
 * distinct from "primary" now that primary is blue.
 */
const nexttab: ThemePalette = {
  bg: '#000000',
  surface: '#111318',
  surfaceAlt: '#161a21',
  surfaceHigh: '#20252e',
  surfaceInset: '#070809',
  border: '#3a4150',
  borderDim: '#252a34',
  borderFocus: '#3b7dff',
  text: '#f7f9fc',
  textDim: '#cbd3e0',
  textMuted: '#8a94a6',
  textInverse: '#ffffff',
  primary: '#3b7dff',
  primaryHover: '#5a92ff',
  primaryDark: '#1f5fe6',
  primaryDim: '#173f9c',
  primaryFaint: '#0e1a3a',
  success: '#22c55e',
  successLight: '#4ade80',
  successFaint: '#0c2f1a',
  danger: '#ef4444',
  dangerDark: '#dc2626',
  dangerFaint: '#3f1212',
  warning: '#f5b300',
  warningLight: '#ffc524',
  info: '#22b8cf',
  infoDark: '#0e94a8',
  purple: '#a855f7',
  white: '#ffffff',
  black: '#000000',
};

export const THEMES: Record<ThemeId, ThemePalette> = { nexttab, warm, hardware, light, midnight };

export const THEME_LIST: ThemeMeta[] = [
  { id: 'nexttab', name: 'NextTab', blurb: 'The brand palette - black, white and cobalt, straight from the logo.', swatch: ['#000000', '#111318', '#3b7dff'] },
  { id: 'hardware', name: 'Hardware', blurb: 'High-contrast dark. Heavy, tactile, terminal-like.', swatch: ['#000000', '#121317', '#f5b300'] },
  { id: 'warm', name: 'Warm', blurb: 'The classic earthy amber palette.', swatch: ['#0c1118', '#1a1610', '#d4a843'] },
  { id: 'light', name: 'Daylight', blurb: 'Bright counter look for daytime service.', swatch: ['#f2f0ec', '#ffffff', '#b8862b'] },
  { id: 'midnight', name: 'Midnight', blurb: 'Colorful indigo with violet + teal accents.', swatch: ['#0b0e1f', '#161a33', '#7c5cff'] },
];

const STORAGE_KEY = 'nt.theme';
const DEFAULT_THEME: ThemeId = 'nexttab';

const listeners = new Set<(id: ThemeId) => void>();
let current: ThemeId = DEFAULT_THEME;

function camelToVar(name: string): string {
  // surfaceHigh -> --c-surface-high
  return '--c-' + name.replace(/[A-Z]/g, (m) => '-' + m.toLowerCase());
}

export function cssVarName(token: string): string {
  return camelToVar(token);
}

function injectPalette(p: ThemePalette): void {
  const root = document.documentElement;
  for (const [k, v] of Object.entries(p)) {
    root.style.setProperty(camelToVar(k), v);
  }
  root.style.setProperty('color-scheme', current === 'light' ? 'light' : 'dark');
}

export function applyTheme(id: ThemeId): void {
  const palette = THEMES[id] ?? THEMES[DEFAULT_THEME];
  current = THEMES[id] ? id : DEFAULT_THEME;
  injectPalette(palette);
  try { localStorage.setItem(STORAGE_KEY, current); } catch { /* ignore */ }
  for (const fn of listeners) { try { fn(current); } catch { /* ignore */ } }
}

/**
 * v0.9.1372: apply a palette WITHOUT remembering it.
 *
 * applyTheme persists to localStorage, which is shared across every tab on
 * this origin - so the back office forcing itself light would have flipped
 * the POS theme too, and the next person to open a station would find
 * their terminal in daylight colours with no idea why.
 *
 * The suite is always light and has no theme picker, so it has nothing to
 * remember. It paints and leaves the saved preference alone.
 */
export function applyThemeEphemeral(id: ThemeId): void {
  const palette = THEMES[id] ?? THEMES[DEFAULT_THEME];
  current = THEMES[id] ? id : DEFAULT_THEME;
  injectPalette(palette);
  for (const fn of listeners) { try { fn(current); } catch { /* ignore */ } }
}

export function getTheme(): ThemeId { return current; }

export function onThemeChange(fn: (id: ThemeId) => void): () => void {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

/** Call once at boot, before first paint, to install the saved theme. */
export function initTheme(): void {
  let saved: ThemeId | null = null;
  try { saved = localStorage.getItem(STORAGE_KEY) as ThemeId | null; } catch { /* ignore */ }
  applyTheme(saved && THEMES[saved] ? saved : DEFAULT_THEME);
}
